import React, { useState, useEffect } from 'react';
import { Card, ListGroup, Accordion, Button } from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { jwtDecode } from 'jwt-decode';
import { useCompanyNavigation } from '../../utils/Hooks/useCompanyNavigation';
import '../Pages.css';

const CustomToggle = ({ children, eventKey }) => {
  const decoratedOnClick = useAccordionButton(eventKey);
  return (
    <Card.Header onClick={decoratedOnClick} style={{ cursor: 'pointer' }}>
      {children}
    </Card.Header>
  );
};

export const ContactCard = ({ contact, eventKey, loading, editContact, addActivity, viewActivity }) => {
  const api = process.env.REACT_APP_API_ENDPOINT;
  const associationsEndpoint = `${api}/v1/associations/company`;
  const getStoredToken = () => localStorage.getItem('token');
  const storedToken = getStoredToken();
  const [toggleAssociations, setToggleAssociations] = useState(false);
  const [associations, setAssociations] = useState([]);
  const toCompany = useCompanyNavigation();

  const showAssociations = async () => {
    setToggleAssociations(!toggleAssociations)
  };

  const parseAssociations = (data, maxDisplay) => {
    const maxloop = maxDisplay ? Math.min(maxDisplay, data.length) : data.length;
    const result = data.slice(0, maxloop).map((item, index) => (
      <span key={index}>
        {item.isDefault ? (
          <b>
            <a className='company-link' href="#" onClick={() => toCompany(item.value)}>
              {item.label}
            </a>
          </b>
        ) : (
          <a className='company-link' href="#" onClick={() => toCompany(item.value)}>
            {item.label}
          </a>
        )}
      </span>
    ));

    if (data.length > maxDisplay) {
      result.push(<span key="more">An additional {data.length - maxDisplay} not shown</span>);
    }
    console.log(result);
    setAssociations(result);
  };

  const showSomeAssociations = () => {
    parseAssociations(contact.Associations);
  };

  const showAllAssociations = async () => {
    try {
      setAssociations([<span key="loading">Loading...</span>]);
      const identity = jwtDecode(storedToken);

      const response = await fetch(`${associationsEndpoint}/${contact.IndividualId}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${storedToken}`,
          EmployeeUID: identity.sub,
          'Content-Type': 'application/json',
        }
      });
      if (response.ok) {
        const data = await response.json();
        parseAssociations(data, 1000);
      } else {
        showSomeAssociations();
        console.error('Failed to fetch associations');
      }
    } catch (error) {
      showSomeAssociations();
      console.error('Failed to fetch associations', error);
    }
  };

  useEffect(() => {
    if (toggleAssociations) {
      showAllAssociations();
    } else {
      showSomeAssociations();
    }
  }, [toggleAssociations]);

  useEffect(() => {
    parseAssociations(contact.Associations);
  }, [contact]);

  const Mailto = ({ email, children }) => <a className="mailto-link" href={`mailto:${email}`}>{children}</a>;
  const Telto = ({ phone, children }) => <a className="telto-link" href={`telto:${phone}`}>{children}</a>;

  return (
    <Accordion defaultActiveKey="0">
      <Card>
        <CustomToggle eventKey={eventKey}>
          <Card.Title className="card-title-has-button">
            <div className='company-title-grid'>
              <div className='contact-title-group-1'>
                {contact.IndividualName}
              </div>
              {contact && 
                <div className='contact-title-group-3'>
                  {contact.IsAssociated && (
                    <>
                      <Button
                        variant="success"
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                        onClick={(e) => viewActivity(e, contact)}
                        className='m-2 edit-button'>
                        <span className="me-2">
                          <i className="bi bi-search"></i>
                        </span>
                        View Activities
                      </Button>

                      <Button
                        variant="success"
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                        onClick={(e) => addActivity(e, contact)}
                        className='m-2 edit-button'>
                        <span className="me-2">
                          <i className="bi bi-calendar-event"></i>
                        </span>
                        Add Activity
                      </Button>
                    </>
                  )}
                  <Button
                    variant="warning"
                    disabled={loading}
                    style={{ borderRadius: "10px" }}
                    onClick={(e) => editContact(e, contact)}
                    className='m-2 edit-button'>
                    <span className="me-2">
                      <i className="bi bi-pencil"></i>
                    </span>
                    Edit
                  </Button>
                </div>
              }
            </div>
          </Card.Title>
        </CustomToggle>
        <Accordion.Collapse eventKey={eventKey}>
          <Card.Body>
            <div className='card-grid'>
              <div className='card-item-1'>
                <ListGroup className="list-group-flush">
                  {contact.IndividualTitle && (
                    <ListGroup.Item>
                      <div className='text-row'>
                        <div className='card-bold'>Title</div>
                        <div className='card-text'>{contact.IndividualTitle}</div>
                      </div>
                    </ListGroup.Item>
                  )}
                  {contact.IndividualEmail && (
                    <ListGroup.Item>
                      <div className='text-row'>
                        <div className='card-bold'>Email</div>
                        <div className='card-text'>
                          <Mailto email={contact.IndividualEmail}>{contact.IndividualEmail}</Mailto>
                        </div>
                      </div>
                    </ListGroup.Item>
                  )}
                  {contact.IndividualPhone && (
                    <ListGroup.Item>
                      <div className='text-row'>
                        <div className='card-bold'>Phone</div>
                        <div className='card-text'>
                          <Telto phone={contact.IndividualPhone}>{contact.IndividualPhone}</Telto>
                        </div>
                      </div>
                    </ListGroup.Item>
                  )}
                  {contact.IndividualCell && (
                    <ListGroup.Item>
                      <div className='text-row'>
                        <div className='card-bold'>Cell</div>
                        <div className='card-text'>
                          <Telto phone={contact.IndividualCell}>{contact.IndividualCell}</Telto>
                        </div>
                      </div>
                    </ListGroup.Item>
                  )}
                  {contact.InternalNotes && (
                    <ListGroup.Item>
                      <div className='text-row'>
                        <div className='card-bold'>Notes</div>
                        <div className='card-text' style={{ whiteSpace: 'pre-line' }}>{contact.InternalNotes}</div>
                      </div>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </div>
              <div className='card-item-2'>
                <div className='d-flex'>
                  <div className='card-bold'>Company Associations</div>
                  <div>
                    {contact.AssociationCount > 5 && (
                      <Button className='e-link' onClick={showAssociations}>
                        + {contact.AssociationCount - 5} more
                      </Button>
                    )}
                  </div>
                </div>
                <ul>
                  {associations.length > 0 ? (
                    associations.map((association, index) => (
                      <li key={index}>{association}</li>
                    ))
                  ) : (
                    'No Associations'
                  )}
                </ul>
              </div>
            </div>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </Accordion>
  );
};
